/**
 * انتخابگر مود روی مسیر `/consult`.
 *
 * ⚠️ **چرا query param و نه یک مسیر تازه:** نشانی `/consult` در bookmark کاربر،
 * لینک‌های ذخیره‌شده و ارجاع‌های داخلی نشسته است. مود به‌عنوان پارامتر، هر دو
 * تجربه را زیر همان نشانی نگه می‌دارد و نشانی قبلی معنای قبلی‌اش را حفظ می‌کند.
 *
 * ⚠️ **چرا انتخابگر اینجاست و نه در چیدمان سراسری یا نوار کنار:** مود، ویژگی
 * *همین صفحه* است. اگر در `AppShell` می‌نشست، در آرشیو و صفحات کارگاه هم دیده
 * می‌شد و کاربر انتظار داشت آن‌ها را هم عوض کند.
 */

import { GitCompareArrows, MessageSquare } from 'lucide-react'
import { useSearchParams } from 'react-router'

import { JudgmentPage } from '@/features/judgment/JudgmentPage'
import { cn } from '@/lib/cn'

import { ConsultationPage } from './ConsultationPage'
import { ChatPage } from './chat/ChatPage'

import type { Scope } from '@/lib/api/endpoints'

type Mode = 'advice' | 'judgment'

interface ConsultationHubPageProps {
  /** کارگاه نمای مرحله‌ای می‌گیرد و مشتری نمای گفت‌وگو. */
  isStudio: boolean
  scope?: Scope
  showDebug?: boolean
}

export const ConsultationHubPage = ({
  isStudio,
  scope,
  showDebug = false,
}: ConsultationHubPageProps) => {
  const [params, setParams] = useSearchParams()
  const mode: Mode = params.get('mode') === 'judgment' ? 'judgment' : 'advice'

  const select = (next: Mode): void => {
    // نشانی جایگزین می‌شود و در تاریخچه انبار نمی‌شود: جابه‌جایی میان دو مود یک
    // ناوبری نیست و نباید دکمه‌ی بازگشت مرورگر را پر کند.
    setParams(
      next === 'judgment' ? { mode: 'judgment' } : {},
      { replace: true },
    )
  }

  return (
    <div className="space-y-5">
      <div
        className="flex flex-wrap gap-1.5 rounded-md border border-line bg-surface-raised p-1.5 shadow-raised"
        role="tablist"
        aria-label="حالت بررسی"
      >
        <ModeTab
          label="مشاوره تصمیم"
          hint="یک تصمیم"
          icon={<MessageSquare className="size-4" aria-hidden />}
          active={mode === 'advice'}
          onSelect={() => {
            select('advice')
          }}
        />
        <ModeTab
          label="قضاوت تصمیم"
          hint="مقایسه‌ی دو تصمیم"
          icon={<GitCompareArrows className="size-4" aria-hidden />}
          active={mode === 'judgment'}
          onSelect={() => {
            select('judgment')
          }}
        />
      </div>

      {/*
        هر مود حالت خودش را در hook خودش نگه می‌دارد. با عوض شدن مود، کامپوننت
        دیگری unmount می‌شود و حالتش می‌رود؛ همین درست است، چون یک مقایسه‌ی
        نیمه‌کاره و یک مشاوره‌ی نیمه‌کاره دو کار متفاوت‌اند و در هم ادغام نمی‌شوند.
      */}
      {mode === 'judgment' ? (
        <JudgmentPage {...(scope === undefined ? {} : { scope })} />
      ) : isStudio ? (
        <ConsultationPage
          {...(scope === undefined ? {} : { scope })}
          showDebug={showDebug}
        />
      ) : (
        <ChatPage />
      )}
    </div>
  )
}

interface ModeTabProps {
  label: string
  hint: string
  icon: React.ReactNode
  active: boolean
  onSelect: () => void
}

const ModeTab = ({ label, hint, icon, active, onSelect }: ModeTabProps) => (
  <button
    type="button"
    role="tab"
    aria-selected={active}
    onClick={onSelect}
    className={cn(
      'press flex min-w-0 flex-1 items-center justify-center gap-2 rounded-sm px-4 py-2.5 text-sm transition-colors',
      active
        ? 'bg-accent-soft text-accent ring-1 ring-inset ring-accent-line'
        : 'text-ink-muted hover:bg-surface-sunken hover:text-ink',
    )}
  >
    <span className={active ? 'text-accent' : 'text-ink-faint'}>{icon}</span>
    <span className="font-semibold">{label}</span>
    <span className="hidden text-xs text-ink-faint sm:inline">· {hint}</span>
  </button>
)
